import { Component, EventEmitter, Injector, OnDestroy, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Auth0Service } from '@lib/auth0';
import { WithDestroy } from '@lib/shared';
import { Observable } from 'rxjs';
import { switchMap, take, takeUntil } from 'rxjs/operators';
import { MetadataService } from '../../../services/metadata.service';
import { UserService } from '../../../user';
import { DialogService } from '../../services/dialog.service';
import { LoanStepperService } from '../../services/loan-stepper.service';
import { PipelineDataResolver } from '../../services/pipeline-data.resolver';
import { PreloadMod } from '../../../app-preloader.module';
import { BsModalService } from '@lib/bs-modal';
import { PreloadRegisteryService } from '@lib/preload';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: '[app-nav-aside]',
  templateUrl: './nav-aside.component.html',
  styleUrls: ['./nav-aside.component.scss']
})
export class NavAsideComponent extends WithDestroy() implements OnInit, OnDestroy {
  @Output() navAsideClose: EventEmitter<any> = new EventEmitter<any>();

  public loans: any[];
  public excludedStagesIds: any[] = [];
  public loan: any;
  public loanSubmitted = false;
  public themeConfig: any;
  public inviteType: string;
  public posConfig: any;
  public logoUrl: any;
  public currentUser: any;
  public hasErrorInStep: any;
  public isBottomNavOpen = false;
  public pendingTasksCount$: Observable<any>;
  public PreApprovalMod = PreloadMod.PRE_APPROVAL;
  public stagesIds = {};
  private isMobile = false;
  private lookups: any;

  constructor(
    private userService: UserService,
    private authService: Auth0Service,
    private router: Router,
    private route: ActivatedRoute,
    private pipelineDataResolverService: PipelineDataResolver,
    private metadataService: MetadataService,
    public loanStepperService: LoanStepperService,
    private dialogService: DialogService,
    private bsModalService: BsModalService,
    private preloadRegisteryService: PreloadRegisteryService,
    private injector: Injector,
    private breakpointObserver: BreakpointObserver,
    private location: Location
  ) {
    super();
    this.themeConfig = JSON.parse(localStorage.getItem('theme'));
    this.lookups = this.route.snapshot.data.enumerations;
  }

  ngOnInit() {
    this.breakpointObserver.observe(['(min-width: 768px)']).subscribe((state: BreakpointState) => {
      this.isMobile = !state.matches;
    });
    const metadataInfo = this.metadataService.metadata;
    if (metadataInfo) {
      this.logoUrl = this.metadataService.metadata.BranchWizeOrgUnit
        ? this.metadataService.metadata.BranchWizeOrgUnit.logoUrl
        : this.metadataService.metadata.WizeOrgUnit.logoUrl;
    }
    this.excludedStagesIds = this.pipelineDataResolverService.closedLoanStages;
    this.stagesIds = this.pipelineDataResolverService.allStagesWithIds;
    this.userService.getActiveUser().subscribe((value) => {
      this.currentUser = value;
      const loanList = value.Loans || this.route.snapshot.data.loans;
      this.loans = loanList.filter((item) => !this.excludedStagesIds.includes(item.CurrentStage.StageId));
    });

    this.posConfig = this.route.snapshot.data.posOrgConfig.posConfig;
    this.route.data
      .pipe(
        switchMap((data: any) => {
          return data.loan.valueChanges;
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((loan: any) => {
        this.loan = loan;
        this.loan.formattedAddress = this.getFormattedAddress(loan.SubjectProperty.Address);
        this.loanStepperService.loanPurpose = loan.TermsOfLoan && loan.TermsOfLoan.LoanPurposeType;
        this.loanSubmitted = !!(loan && loan.appSubmissionDate);
        const selectedLoan = this.loans.find((ln) => ln.LoanId === this.loan.id);
        this.inviteType = selectedLoan ? selectedLoan.inviteType : null;
      });
    this.pendingTasksCount$ = this.loanStepperService.pendingTasksData$;
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  public selectLoan(selectedloan: any) {
    const loNmls = selectedloan.LoanOfficer?.nmlsId;
    this.navClose();
    if (selectedloan.LoanId !== this.loan.id) {
      this.router.navigate(loNmls ? [`/${loNmls}/pos/app/${selectedloan.LoanId}`] : [`/pos/app/${selectedloan.LoanId}`]);
      this.location.replaceState(loNmls ? `/${loNmls}/pos/app/${selectedloan.LoanId}` : `/pos/app/${selectedloan.LoanId}`);
    }
  }

  public logout() {
    const uri = '/' + (this.route.snapshot.params.nmls || '');
    if (this.loanStepperService.stopNavigation) {
      const modalRef = this.dialogService.open('confirm-dialog', {
        title: 'Unsaved Changes',
        message: 'Unsaved data will be lost. Are you sure you want to continue?'
      });
      modalRef.onClose.subscribe((res) => {
        if (res) {
          this.loanStepperService.stopNavigation = false;
          this.authService.logout({ redirectUri: uri });
        }
      });
    } else {
      this.authService.logout({ redirectUri: uri });
    }
  }

  public navClose() {
    this.isBottomNavOpen = false;
    this.navAsideClose.emit();
  }

  public openPreApprovalDialog(loan: any) {
    let modalOptions: any = {};
    if (this.isMobile) {
      modalOptions.align = 'bottom';
      modalOptions.style = '--modal-gutter: 0; --modal-radius: 0; --footer-height: 60px';
    }
    this.preloadRegisteryService
      .import(this.PreApprovalMod)
      .pipe(take(1))
      .subscribe((PreviewPreapprovalComponent) => {
        const modalRef = this.bsModalService.open(PreviewPreapprovalComponent, {
          modalOptions: modalOptions,
          injector: this.injector
        });
        modalRef.componentRef.instance.loan = loan;
        modalRef.componentRef.instance.realtorConfig = loan.LetterConstraints = {
          Permission: loan.CurrentStage.StageId === this.stagesIds['PREAPPROVED'] ? true : null
        };
        modalRef.componentRef.instance.orgId = loan.WizeOrgUnitId;
        modalRef.componentRef.instance.loanBorrowers = loan.Borrowers;
        modalRef.componentRef.instance.mortgageType = loan.TermsOfLoan.MortgageType;
        modalRef.componentRef.instance.orgConfig = this.route.snapshot.data.posOrgConfig.realtorConfig.preApproval;
        modalRef.componentRef.instance.lookups = this.lookups;
        this.navClose();
      });
  }

  private getFormattedAddress(address: any) {
    return (
      (address.CityName ? address.CityName + ', ' : '') +
      (address.StateCode ? address.StateCode : '') +
      (address.PostalCode ? ' ' + address.PostalCode + ', USA' : '')
    );
  }

  private getRealtorConfig(loan) {
    return (loan.LetterConstraints = { Permission: loan.InApplicationStatus ? true : null });
  }
}
